import useAppLanguage from '../i18n';
import { translationKey, readAppLanguage } from '../i18n';
import { localize } from '../i18n';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import Dialog from '../components/StableDialog';
import LoadingLabel from '../components/LoadingLabel';
import PageLoading from '../components/PageLoading';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, TextField, Fab, CircularProgress, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TuneIcon from '@mui/icons-material/Tune';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PageHeader from '../components/PageHeader';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import BottomSheet from '../components/BottomSheet';
import AppToast from '../components/AppToast';
import { getDeviceUuid, getSymptoms, createSymptom, updateSymptom, deleteSymptom, getMedications } from '../api';

const SEVERITY_OPTIONS = [
    { value: 1, label: translationKey('약함'), icon: SentimentSatisfiedIcon, color: '#22c55e' },
    { value: 3, label: translationKey('보통'), icon: SentimentNeutralIcon, color: '#facc15' },
    { value: 5, label: translationKey('심함'), icon: SentimentVeryDissatisfiedIcon, color: '#ef4444' },
];

const COMMON_SYMPTOMS = [translationKey('두통'), translationKey('어지러움'), translationKey('속쓰림'), translationKey('구역질'), translationKey('졸림'), translationKey('발진'), translationKey('설사'), translationKey('변비'), translationKey('식욕 변화'), translationKey('피로감')];

const EFFECT_DURATION_OPTIONS = [
    { value: 120, label: translationKey('2시간') },
    { value: 240, label: translationKey('4시간') },
    { value: 360, label: translationKey('6시간') },
    { value: 'CUSTOM', label: translationKey('직접') },
];

const DATE_FILTERS = [
    { value: 'ALL', label: translationKey('전체') },
    { value: 'TODAY', label: translationKey('오늘') },
    { value: '7D', label: translationKey('7일') },
    { value: '30D', label: translationKey('30일') },
    { value: 'CUSTOM', label: translationKey('직접') },
];

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const todayString = () => formatDate(new Date());

const splitSymptomText = (text = '') => text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const formatEffectDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return localize("{v0}시간 {v1}분", { v0: hours, v1: mins });
    if (hours > 0) return localize("{v0}시간", { v0: hours });
    return localize("{v0}분", { v0: mins });
};

export default function SymptomPage() {
    const { language } = useAppLanguage();
    const [symptoms, setSymptoms] = useState([]);
    const [medications, setMedications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // 삭제 컨펌 대상 symptom
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [filterMedId, setFilterMedId] = useState(null);
    const [filterSymptom, setFilterSymptom] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [showAllSymptomFilters, setShowAllSymptomFilters] = useState(false);
    const [dateFilter, setDateFilter] = useState('ALL');
    const [customStartDate, setCustomStartDate] = useState(todayString);
    const [customEndDate, setCustomEndDate] = useState(todayString);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const PAGE_SIZE = 50;
    const sentinelRef = useRef(null);
    const fetchedOnceRef = useRef(false);
    const requestIdRef = useRef(0);
    const composingRef = useRef(false);

    // Form state
    const [editingId, setEditingId] = useState(null);
    const [selectedSymptoms, setSelectedSymptoms] = useState([]);
    const [customSymptom, setCustomSymptom] = useState('');
    const [severity, setSeverity] = useState(3);
    const [selectedMedIds, setSelectedMedIds] = useState([]);
    const [effectDurationPreset, setEffectDurationPreset] = useState(null);
    const [customEffectHours, setCustomEffectHours] = useState('');
    const [customEffectMinutes, setCustomEffectMinutes] = useState('');
    const [memo, setMemo] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const dateRange = useMemo(() => {
        const today = new Date();
        if (dateFilter === 'TODAY') {
            const date = formatDate(today);
            return { startDate: date, endDate: date };
        }
        if (dateFilter === '7D') {
            return { startDate: formatDate(addDays(today, -6)), endDate: formatDate(today) };
        }
        if (dateFilter === '30D') {
            return { startDate: formatDate(addDays(today, -29)), endDate: formatDate(today) };
        }
        if (dateFilter === 'CUSTOM' && customStartDate && customEndDate) {
            return customStartDate <= customEndDate
                ? { startDate: customStartDate, endDate: customEndDate }
                : { startDate: customEndDate, endDate: customStartDate };
        }
        return null;
    }, [customEndDate, customStartDate, dateFilter]);

    const queryDateRange = useMemo(() => {
        if (dateRange) return dateRange;
        if (filterMedId) {
            return { startDate: '1970-01-01', endDate: todayString() };
        }
        return null;
    }, [dateRange, filterMedId]);

    const customDateIncomplete = dateFilter === 'CUSTOM' && (!customStartDate || !customEndDate);

    const fetchData = useCallback(async () => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setLoadError(false);
        if (!fetchedOnceRef.current) {
            setLoading(true);
        }
        setLoadingMore(false);
        try {
            const uuid = getDeviceUuid();
            if (customDateIncomplete) {
                const medsData = await getMedications(uuid);
                if (requestId !== requestIdRef.current) return;
                setSymptoms([]);
                setMedications(medsData);
                setHasMore(false);
                return;
            }
            const [symptomsData, medsData] = await Promise.all([
                queryDateRange ? getSymptoms(uuid, queryDateRange) : getSymptoms(uuid, 0, PAGE_SIZE),
                getMedications(uuid),
            ]);
            if (requestId !== requestIdRef.current) return;
            setSymptoms(symptomsData);
            setMedications(medsData);
            setHasMore(!queryDateRange && symptomsData.length >= PAGE_SIZE);
        } catch (err) {
            console.error('Failed to load symptoms:', err);
            if (requestId === requestIdRef.current) setLoadError(true);
        } finally {
            if (requestId === requestIdRef.current) {
                fetchedOnceRef.current = true;
                setLoading(false);
            }
        }
    }, [customDateIncomplete, queryDateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // 무한스크롤: sentinel 요소가 viewport에 보이면 다음 페이지 fetch
    useEffect(() => {
        if (queryDateRange || !hasMore || loading) return;
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(async ([entry]) => {
            if (!entry.isIntersecting || loadingMore || !hasMore) return;
            const requestId = requestIdRef.current;
            setLoadingMore(true);
            try {
                const uuid = getDeviceUuid();
                const next = await getSymptoms(uuid, symptoms.length, PAGE_SIZE);
                if (requestId !== requestIdRef.current) return;
                setSymptoms((prev) => [...prev, ...next]);
                setHasMore(next.length >= PAGE_SIZE);
            } catch (err) {
                console.error('Failed to load more symptoms:', err);
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoadingMore(false);
                }
            }
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore, queryDateRange, symptoms.length]);

    const personalSymptoms = useMemo(() => {
        const counts = new Map();
        symptoms.forEach((item) => {
            splitSymptomText(item.symptom).forEach((name) => {
                if (COMMON_SYMPTOMS.includes(name)) return;
                counts.set(name, (counts.get(name) || 0) + 1);
            });
        });
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name]) => name);
    }, [symptoms]);

    const symptomChoices = useMemo(() => {
        const merged = [...personalSymptoms, ...COMMON_SYMPTOMS.map(name => localize(name))];
        return merged.filter((name, index) => merged.indexOf(name) === index);
    }, [personalSymptoms, language]);

    const symptomQuery = customSymptom.trim();

    const symptomSuggestions = useMemo(() => {
        const normalizedQuery = symptomQuery.toLowerCase();
        const base = symptomChoices
            .filter((name) => !selectedSymptoms.includes(name))
            .filter((name) => !normalizedQuery || name.toLowerCase().includes(normalizedQuery))
            .map((name) => ({
                type: personalSymptoms.includes(name) ? translationKey('내 기록') : translationKey('추천'),
                name,
            }));

        if (symptomQuery && !selectedSymptoms.includes(symptomQuery) && !symptomChoices.includes(symptomQuery)) {
            return [{ type: translationKey('새 증상'), name: symptomQuery }, ...base];
        }
        return base;
    }, [personalSymptoms, selectedSymptoms, symptomChoices, symptomQuery]);

    const getSymptomParts = () => {
        const parts = [...selectedSymptoms];
        splitSymptomText(customSymptom).forEach((name) => {
            if (!parts.includes(name)) parts.push(name);
        });
        return parts;
    };

    const getSymptomText = () => getSymptomParts().join(', ');

    const getEffectDurationMinutes = () => {
        if (selectedMedIds.length === 0) return null;
        if (typeof effectDurationPreset === 'number') return effectDurationPreset;
        if (effectDurationPreset !== 'CUSTOM') return null;
        const hours = parseInt(customEffectHours, 10) || 0;
        const minutes = parseInt(customEffectMinutes, 10) || 0;
        const total = (hours * 60) + minutes;
        if (total <= 0) return null;
        return Math.min(total, 1440);
    };

    const addSymptom = (name) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        setSelectedSymptoms(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
        setCustomSymptom('');
    };

    const removeSymptom = (name) => {
        setSelectedSymptoms(prev => prev.filter(v => v !== name));
    };

    const handleEdit = (s) => {
        const parts = splitSymptomText(s.symptom);
        const known = parts.filter(p => symptomChoices.includes(p));
        const custom = parts.filter(p => !symptomChoices.includes(p)).join(', ');
        setEditingId(s.id);
        setSelectedSymptoms(known);
        setCustomSymptom(custom);
        setSeverity(s.severity);
        setSelectedMedIds(s.medicationIds || (s.medicationId ? [s.medicationId] : []));
        if (s.effectDurationMinutes) {
            const preset = EFFECT_DURATION_OPTIONS.find(option => option.value === s.effectDurationMinutes);
            if (preset) {
                setEffectDurationPreset(preset.value);
                setCustomEffectHours('');
                setCustomEffectMinutes('');
            } else {
                setEffectDurationPreset('CUSTOM');
                setCustomEffectHours(String(Math.floor(s.effectDurationMinutes / 60)));
                setCustomEffectMinutes(String(s.effectDurationMinutes % 60));
            }
        } else {
            setEffectDurationPreset(null);
            setCustomEffectHours('');
            setCustomEffectMinutes('');
        }
        setMemo(s.memo || '');
        setShowDialog(true);
    };

    const handleSubmit = async () => {
        const text = getSymptomText();
        if (!text) return;
        setSubmitting(true);
        try {
            const uuid = getDeviceUuid();
            const payload = {
                symptom: text,
                severity,
                medicationId: selectedMedIds.length === 1 ? selectedMedIds[0] : null,
                medicationIds: selectedMedIds.length > 0 ? selectedMedIds : null,
                effectDurationMinutes: getEffectDurationMinutes(),
                memo: memo.trim() || null,
            };
            if (editingId) {
                await updateSymptom(uuid, editingId, payload);
            } else {
                await createSymptom(uuid, payload);
            }
            setSnackbar({ open: true, message: editingId ? localize("수정되었어요") : localize("증상이 기록되었어요"), severity: 'success' });
            setShowDialog(false);
            resetForm();
            fetchData();
        } catch (err) {
            setSnackbar({ open: true, message: localize("기록 실패"), severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const [deleting, setDeleting] = useState(false);
    const handleDelete = async (id) => {
        if (deleting) return;
        setDeleting(true);
        try {
            const uuid = getDeviceUuid();
            await deleteSymptom(uuid, id);
            setSymptoms(prev => prev.filter(s => s.id !== id));
            setDeleteTarget(null);
            setSnackbar({ open: true, message: localize("삭제되었어요"), severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: localize("삭제 실패"), severity: 'error' });
        } finally { setDeleting(false); }
    };

    const resetForm = () => {
        setEditingId(null);
        setSelectedSymptoms([]);
        setCustomSymptom('');
        setSeverity(3);
        setSelectedMedIds([]);
        setEffectDurationPreset(null);
        setCustomEffectHours('');
        setCustomEffectMinutes('');
        setMemo('');
    };

    const getSeverityInfo = (val) => SEVERITY_OPTIONS.find(s => s.value === val) || SEVERITY_OPTIONS[1];

    const selectedMedication = useMemo(
        () => medications.find(med => med.id === filterMedId) || null,
        [filterMedId, medications]
    );

    const medFilteredSymptoms = useMemo(() => {
        if (!filterMedId) return symptoms;
        return symptoms.filter(s => (s.medicationIds || [s.medicationId]).filter(Boolean).includes(filterMedId));
    }, [filterMedId, symptoms]);

    const allSymptomFilterOptions = useMemo(() => {
        const counts = new Map();
        medFilteredSymptoms.forEach((item) => {
            splitSymptomText(item.symptom).forEach((name) => {
                counts.set(name, (counts.get(name) || 0) + 1);
            });
        });
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => ({ name, count }));
    }, [medFilteredSymptoms]);

    const symptomFilterOptions = useMemo(
        () => showAllSymptomFilters ? allSymptomFilterOptions : allSymptomFilterOptions.slice(0, 4),
        [allSymptomFilterOptions, showAllSymptomFilters]
    );

    const filteredSymptoms = useMemo(() => {
        if (!filterSymptom) return medFilteredSymptoms;
        return medFilteredSymptoms.filter(s => splitSymptomText(s.symptom).includes(filterSymptom));
    }, [filterSymptom, medFilteredSymptoms]);

    const listSummary = useMemo(() => {
        const symptomCounts = new Map();
        filteredSymptoms.forEach((item) => {
            splitSymptomText(item.symptom).forEach((name) => {
                symptomCounts.set(name, (symptomCounts.get(name) || 0) + 1);
            });
        });
        const topSymptom = Array.from(symptomCounts.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0] || null;
        const avgSeverity = filteredSymptoms.length > 0
            ? filteredSymptoms.reduce((sum, item) => sum + item.severity, 0) / filteredSymptoms.length
            : null;
        const avgSeverityLabel = avgSeverity == null
            ? null
            : avgSeverity < 2.4 ? translationKey('약함') : avgSeverity < 4 ? translationKey('보통') : translationKey('심함');
        return {
            count: filteredSymptoms.length,
            topSymptomName: topSymptom?.[0] || null,
            topSymptomCount: topSymptom?.[1] || 0,
            uniqueSymptomCount: symptomCounts.size,
            avgSeverityLabel,
        };
    }, [filteredSymptoms]);

    const editingSymptom = useMemo(
        () => symptoms.find(item => item.id === editingId) || null,
        [editingId, symptoms]
    );

    const groupedSymptoms = useMemo(() => {
        const grouped = {};
        filteredSymptoms.forEach(s => {
            const date = s.date;
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(s);
        });
        return grouped;
    }, [filteredSymptoms]);

    const dateKeys = useMemo(
        () => Object.keys(groupedSymptoms).sort((a, b) => b.localeCompare(a)),
        [groupedSymptoms]
    );

    const filterSummaryText = [
        localize(dateFilter === 'ALL' ? '전체 기간' : DATE_FILTERS.find(option => option.value === dateFilter)?.label),
        selectedMedication?.name || localize('모든 약'),
        filterSymptom || null,
    ].filter(Boolean).join(' · ');

    const hasActiveFilter = dateFilter !== 'ALL' || !!filterMedId || !!filterSymptom;

    const clearFilters = () => {
        setDateFilter('ALL');
        setFilterMedId(null);
        setFilterSymptom(null);
        setShowAllSymptomFilters(false);
    };

    const emptyMessage = hasActiveFilter
        ? translationKey('조건에 맞는 증상 기록이 없어요')
        : translationKey('기록된 증상이 없어요');


    if (loading) {
        return (
            <PageLoading />
        );
    }

    return (
        <Box sx={{ pb: 10 }}>
            <PageHeader back title={localize("증상 기록")} />

            {loadError && (
                <Box sx={{ textAlign: 'center', px: 3, py: 3 }}>
                    <SingleLineText sx={{ mb: 2 }}>{localize("증상 기록을 불러오지 못했어요")}</SingleLineText>
                    <Button variant="contained" onClick={fetchData}>{localize("다시 불러오기")}</Button>
                </Box>
            )}

            {(symptoms.length > 0 || medications.length > 0 || hasActiveFilter) && (
                <Box sx={{ px: 2, pt: 3 }}>
                    <Box sx={{
                        bgcolor: '#fffdf2', border: '1px solid #f6e7a8', borderRadius: 3,
                        p: 1.5, boxShadow: '0 6px 18px rgba(90, 74, 0, 0.06)',
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.2 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{
                                    fontSize: '0.9rem', color: '#8a7b46', fontWeight: 600, mb: 0.2,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    ...(readAppLanguage() !== 'ko' ? { overflow: 'visible', textOverflow: 'clip', whiteSpace: 'normal', overflowWrap: 'anywhere' } : {}),
                                }}>
                                    {filterSummaryText}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '1.2rem', color: '#1a1a1a', fontWeight: 700, lineHeight: 1.3,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {localize("{count}개 기록", { count: listSummary.count })}<Typography component="span" sx={{ fontSize: '1rem', color: '#9b8d55', fontWeight: 600 }}>
                                        {listSummary.topSymptomCount > 1
                                            ? localize(" · {v0} {v1}번", { v0: listSummary.topSymptomName, v1: listSummary.topSymptomCount })
                                            : listSummary.uniqueSymptomCount > 0
                                                ? localize(" · {v0}가지 증상", { v0: listSummary.uniqueSymptomCount })
                                                : ''}
                                    </Typography>
                                </Typography>
                                {listSummary.avgSeverityLabel && (
                                    <Typography sx={{ fontSize: '0.92rem', color: '#8a7b46', fontWeight: 600, mt: 0.35 }}>{localize("평균 불편함은 ")}{localize(listSummary.avgSeverityLabel)}{localize("이에요")}</Typography>
                                )}
                            </Box>
                            <Button
                                size="small"
                                startIcon={<TuneIcon sx={{ fontSize: 17 }} />}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setFiltersExpanded(prev => !prev)}
                                sx={{
                                    flexShrink: 0, minWidth: 0, px: 1.1, py: 0.6, borderRadius: 2,
                                    color: filtersExpanded || hasActiveFilter ? '#5a4a00' : '#777',
                                    bgcolor: filtersExpanded || hasActiveFilter ? '#ffe566' : '#fff',
                                    fontWeight: 700,
                                    '& .MuiButton-startIcon': { mr: 0.4 },
                                    '&:hover': { bgcolor: filtersExpanded || hasActiveFilter ? '#f4d84f' : '#f5f5f5' },
                                }}
                            >{localize("필터")}</Button>
                        </Box>

                        {filtersExpanded && (
                            <Box sx={{ mt: 1.4, pt: 1.2, borderTop: '1px solid #f5f5f5' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.7 }}>
                                    <SingleLineText sx={{ fontSize: '0.9rem', color: '#999', fontWeight: 700 }}>{localize("기간")}</SingleLineText>
                                    {hasActiveFilter && (
                                        <SingleLineText
                                            role="button"
                                            tabIndex={0}
                                            onClick={clearFilters}
                                            sx={{ fontSize: '0.9rem', color: '#999', fontWeight: 700, cursor: 'pointer' }}
                                        >{localize("초기화")}</SingleLineText>
                                    )}
                                </Box>
                                <Box sx={{
                                            display: 'flex', gap: 0.6, overflowX: 'auto', pb: 0.2, mb: dateFilter === 'CUSTOM' ? 1.6 : 1.3,
                                            WebkitOverflowScrolling: 'touch',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}>
                                    {DATE_FILTERS.map(option => {
                                        const selected = dateFilter === option.value;
                                        return (
                                            <Box
                                                key={option.value}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setDateFilter(option.value)}
                                                sx={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    minWidth: 50, px: 1.4, py: 0.65, borderRadius: '18px',
                                                    flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                    bgcolor: selected ? '#ffe566' : '#f5f5f5',
                                                    color: selected ? '#5a4a00' : '#666',
                                                    fontWeight: selected ? 700 : 600,
                                                    fontSize: '0.98rem',
                                                }}
                                            >
                                                {localize(option.label)}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {dateFilter === 'CUSTOM' && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                                        <TextField
                                            type="date"
                                            size="small"
                                            label={localize("시작")}
                                            value={customStartDate}
                                            onChange={(e) => setCustomStartDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fafafa' } }}
                                        />
                                        <TextField
                                            type="date"
                                            size="small"
                                            label={localize("끝")}
                                            value={customEndDate}
                                            onChange={(e) => setCustomEndDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fafafa' } }}
                                        />
                                    </Box>
                                )}

                                {symptomFilterOptions.length > 0 && (
                                    <>
                                        <SingleLineText sx={{ fontSize: '0.9rem', color: '#999', fontWeight: 700, mb: 0.7 }}>{localize("증상")}</SingleLineText>
                                        <Box sx={{
                                            display: 'flex', gap: 0.6, overflowX: 'auto', pb: 0.7, mb: 0.6,
                                            WebkitOverflowScrolling: 'touch',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}>
                                            <Box
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setFilterSymptom(null)}
                                                sx={{
                                                    display: 'inline-flex', alignItems: 'center',
                                                    px: 1.4, py: 0.65, borderRadius: '18px',
                                                    flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                    bgcolor: filterSymptom === null ? '#ffe566' : '#f5f5f5',
                                                    color: filterSymptom === null ? '#5a4a00' : '#666',
                                                    fontWeight: filterSymptom === null ? 700 : 600,
                                                    fontSize: '0.98rem',
                                                }}
                                            >{localize(translationKey("전체"))}</Box>
                                            {symptomFilterOptions.map(option => {
                                                const selected = filterSymptom === option.name;
                                                return (
                                                    <Box
                                                        key={option.name}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => setFilterSymptom(selected ? null : option.name)}
                                                        sx={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            maxWidth: 150, px: 1.4, py: 0.65, borderRadius: '18px',
                                                            flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                            bgcolor: selected ? '#ffe566' : '#f5f5f5',
                                                            color: selected ? '#5a4a00' : '#666',
                                                            fontWeight: selected ? 700 : 600,
                                                            fontSize: '0.98rem', whiteSpace: 'nowrap',
                                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {option.name}
                                                    </Box>
                                                );
                                            })}
                                            {allSymptomFilterOptions.length > 4 && (
                                                <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setShowAllSymptomFilters(prev => !prev)}
                                                    sx={{
                                                        display: 'inline-flex', alignItems: 'center',
                                                        px: 1.4, py: 0.65, borderRadius: '18px',
                                                        flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                        bgcolor: '#fff',
                                                        color: '#777',
                                                        border: '1px solid #f0f0f0',
                                                        fontWeight: 600,
                                                        fontSize: '0.98rem', whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {showAllSymptomFilters ? localize("접기") : localize("더보기 {v0}", { v0: allSymptomFilterOptions.length - 4 })}
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}

                                {medications.length > 0 && (
                                    <>
                                        <SingleLineText sx={{ fontSize: '0.9rem', color: '#999', fontWeight: 700, mb: 0.7 }}>{localize("약")}</SingleLineText>
                                        <Box sx={{
                                            display: 'flex', gap: 0.6, overflowX: 'auto', pb: 0.7,
                                            WebkitOverflowScrolling: 'touch',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}>
                                            <Box
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setFilterMedId(null)}
                                                sx={{
                                                    display: 'inline-flex', alignItems: 'center',
                                                    px: 1.4, py: 0.65, borderRadius: '18px',
                                                    flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                    bgcolor: filterMedId === null ? '#ffe566' : '#f5f5f5',
                                                    color: filterMedId === null ? '#5a4a00' : '#666',
                                                    fontWeight: filterMedId === null ? 700 : 600,
                                                    fontSize: '0.98rem',
                                                }}
                                            >{localize("모든 약")}</Box>
                                            {medications.map(med => {
                                                const selected = filterMedId === med.id;
                                                return (
                                                    <Box
                                                        key={med.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => setFilterMedId(selected ? null : med.id)}
                                                        sx={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            maxWidth: 170, px: 1.4, py: 0.65, borderRadius: '18px',
                                                            flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                            bgcolor: selected ? '#ffe566' : '#f5f5f5',
                                                            color: selected ? '#5a4a00' : '#666',
                                                            fontWeight: selected ? 700 : 600,
                                                            fontSize: '0.98rem', whiteSpace: 'nowrap',
                                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {med.name}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            <Box sx={{ px: 2, mt: 2, pb: 12 }}>
                {symptoms.length === 0 && !hasActiveFilter && !loadError ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <EditNoteIcon sx={{ fontSize: 48, mb: 2, color: '#8a7000' }} />
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a1a', mb: 0.5 }}>{localize(translationKey("기록된 증상이 없어요"))}</SingleLineText>
                        <SingleLineText sx={{ color: '#777', fontSize: '1rem', mb: 3 }}>{localize("복용 후 느끼는 증상을 기록해보세요")}</SingleLineText>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setShowDialog(true)}
                            sx={{
                                bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' },
                                color: '#5a4a00', borderRadius: 3, px: 4, py: 1.5,
                            }}
                        >{localize("증상 기록하기")}</Button>
                    </Box>
                ) : filteredSymptoms.length === 0 && !loadError ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#777', mb: 0.5 }}>
                            {localize(emptyMessage)}
                        </Typography>
                        {hasActiveFilter && (
                            <SingleLineText sx={{ color: '#aaa', fontSize: '0.98rem' }}>{localize("기간이나 약을 바꿔서 다시 볼 수 있어요")}</SingleLineText>
                        )}
                    </Box>
                ) : (
                        <>
                            {dateKeys.map(date => {
                                const [, m, d] = date.split('-').map(Number);
                                return (
                                    <Box key={date} sx={{ mb: 2.5 }}>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#999', mb: 1 }}>
                                            {m}{localize("월 ")}{d}{localize("일")}</Typography>
                                        {groupedSymptoms[date].map(s => {
                                            const sev = getSeverityInfo(s.severity);
                                            const medicationLabel = (s.medicationNames || [s.medicationName]).filter(Boolean).join(', ');
                                            const metaParts = [
                                                localize(sev.label),
                                                medicationLabel || null,
                                                s.effectDurationMinutes ? localize("복용 후 {v0} 뒤 약효 떨어짐", { v0: formatEffectDuration(s.effectDurationMinutes) }) : null,
                                            ].filter(Boolean);
                                            return (
                                                <Box key={s.id} role="button" tabIndex={0} onClick={() => handleEdit(s)} sx={{
                                                    position: 'relative',
                                                    p: 1.45, pr: 4.4, pl: 1.65, mb: 0.9, borderRadius: 3,
                                                    bgcolor: '#fff', border: '1px solid #ececec',
                                                    borderLeft: `4px solid ${sev.color}`,
                                                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)',
                                                    cursor: 'pointer', '&:active': { opacity: 0.7 },
                                                }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{
                                                            fontWeight: 700, fontSize: '1.06rem', color: '#1a1a1a', lineHeight: 1.35,
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {s.symptom}
                                                        </Typography>
                                                        <Typography sx={{
                                                            mt: 0.35, fontSize: '0.94rem', color: '#555', fontWeight: 500, lineHeight: 1.35,
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {metaParts.join(' · ')}
                                                        </Typography>
                                                        {s.memo && (
                                                            <Typography sx={{
                                                                mt: 0.55,
                                                                fontSize: '0.9rem', color: '#777', lineHeight: 1.35,
                                                                whiteSpace: 'pre-line',
                                                                overflow: 'hidden',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                            }}>
                                                                {s.memo}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <ChevronRightIcon sx={{
                                                        position: 'absolute', right: 1.4, top: '50%', transform: 'translateY(-50%)',
                                                        color: '#d0d0d0', fontSize: 21,
                                                    }} />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                            {/* 무한스크롤 sentinel — viewport에 보이면 다음 페이지 fetch */}
                            {hasMore && (
                                <Box ref={sentinelRef} sx={{ textAlign: 'center', py: 2 }}>
                                    {loadingMore && (
                                        <CircularProgress size={20} sx={{ color: '#8a7000' }} />
                                    )}
                                </Box>
                            )}
                        </>
                )}
            </Box>

            {/* Create/Edit BottomSheet */}
            <BottomSheet
                dismissDisabled={submitting}
                open={showDialog}
                onClose={() => { setShowDialog(false); resetForm(); }}
                footer={
                    <Button
                        variant="contained"
                        fullWidth
                        onMouseDown={(e) => e.preventDefault()}
                        disabled={!getSymptomText() || submitting}
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3,
                            py: 1.25, fontWeight: 700, fontSize: '1.05rem',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                            '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                        }}
                    >
                        {submitting ? <CircularProgress size={24} sx={{ color: '#5a4a00' }} /> : editingId ? localize('수정하기') : localize('기록하기')}
                    </Button>
                }
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                    <SingleLineText sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a' }}>
                        {editingId ? localize("증상 수정") : localize("증상 기록")}
                    </SingleLineText>
                    {editingId && (
                        <Button
                            size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                                const target = editingSymptom || { id: editingId };
                                setShowDialog(false);
                                setDeleteTarget(target);
                                resetForm();
                            }}
                            sx={{
                                minWidth: 0,
                                px: 1,
                                py: 0.45,
                                color: '#ef4444',
                                border: '1px solid #fecaca',
                                bgcolor: '#fff',
                                fontWeight: 600,
                                fontSize: '0.92rem',
                                borderRadius: 2,
                                '&:hover': { bgcolor: '#fff5f5', borderColor: '#fca5a5' },
                            }}
                        >{localize("삭제")}</Button>
                    )}
                </Box>
                <SingleLineText sx={{ fontSize: '0.92rem', color: '#999', fontWeight: 500, mb: 1.8 }}>{localize("입력하거나 추천에서 골라주세요")}</SingleLineText>

                <Box sx={{ mb: 1.8 }}>
                    <SingleLineText sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1a1a', mb: 0.8 }}>{localize("어떤 증상이 있었나요?")}</SingleLineText>
                    <TextField
                        fullWidth
                        placeholder={localize("증상 검색 또는 직접 입력")}
                        value={customSymptom}
                        onChange={(e) => setCustomSymptom(e.target.value)}
                        onCompositionStart={() => { composingRef.current = true; }}
                        onCompositionEnd={() => { composingRef.current = false; }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (composingRef.current || e.nativeEvent?.isComposing || e.keyCode === 229) return;
                                addSymptom(customSymptom);
                            }
                        }}
                        size="small"
                        InputProps={{
                            endAdornment: symptomQuery ? (
                                <Button
                                    size="small"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => addSymptom(customSymptom)}
                                    sx={{
                                        minWidth: 0,
                                        px: 1.1,
                                        py: 0.45,
                                        mr: -0.4,
                                        borderRadius: 2,
                                        bgcolor: '#ffe566',
                                        color: '#5a4a00',
                                        fontWeight: 700,
                                        fontSize: '0.88rem',
                                        '&:hover': { bgcolor: '#f4d84f' },
                                    }}
                                >{localize("추가")}</Button>
                            ) : null,
                        }}
                        sx={{
                            mb: selectedSymptoms.length > 0 ? 1 : 0.8,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                bgcolor: '#f9fafb',
                                fontSize: '1.05rem',
                            },
                        }}
                    />

                    {selectedSymptoms.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1 }}>
                            {selectedSymptoms.map((name) => (
                                <Box
                                    key={name}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => removeSymptom(name)}
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        px: 1.1, py: 0.45, borderRadius: '16px',
                                        bgcolor: '#fff7bf', color: '#5a4a00',
                                        border: '1px solid #f4d84f',
                                        fontWeight: 600, fontSize: '0.95rem',
                                        cursor: 'pointer', userSelect: 'none',
                                    }}
                                >
                                    {name}
                                    <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1, color: '#8a7000' }}>
                                        ×
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box sx={{
                        border: '1px solid #f2f2f2',
                        borderRadius: 3,
                        overflowY: 'auto',
                        maxHeight: 42 * 4,
                        WebkitOverflowScrolling: 'touch',
                        bgcolor: '#fff',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                        {symptomSuggestions.length > 0 ? symptomSuggestions.map((item, index) => (
                            <Box
                                key={`${localize(item.type)}-${item.name}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => addSymptom(item.name)}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    minHeight: 42, px: 1.3,
                                    borderTop: index === 0 ? 'none' : '1px solid #f5f5f5',
                                    cursor: 'pointer',
                                    '&:active': { bgcolor: '#fafafa' },
                                }}
                            >
                                <Typography sx={{ fontSize: '1.02rem', color: '#1a1a1a', fontWeight: 600 }}>
                                    {item.name}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '0.82rem',
                                    color: item.type === translationKey('새 증상') ? '#8a7000' : '#aaa',
                                    fontWeight: 600,
                                }}>
                                    {localize(item.type)}
                                </Typography>
                            </Box>
                        )) : (
                            <Box sx={{ minHeight: 44, px: 1.4, display: 'flex', alignItems: 'center' }}>
                                <SingleLineText sx={{ fontSize: '0.95rem', color: '#999', fontWeight: 600 }}>{localize("입력하면 새 증상으로 저장돼요")}</SingleLineText>
                            </Box>
                        )}
                    </Box>
                </Box>

                <SingleLineText sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1a1a', mb: 1 }}>{localize("얼마나 불편했나요?")}</SingleLineText>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.7, mb: 1.8 }}>
                    {SEVERITY_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const selected = severity === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                role="button"
                                onClick={() => { document.activeElement?.blur(); setSeverity(opt.value); }}
                                sx={{
                                    minHeight: 58,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', borderRadius: 3,
                                    bgcolor: selected ? `${opt.color}16` : '#f9fafb',
                                    border: selected ? `1.5px solid ${opt.color}` : '1px solid #f0f0f0',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <Icon sx={{ fontSize: 24, color: selected ? opt.color : '#b8b8b8' }} />
                                <SingleLineText sx={{
                                    fontSize: '0.98rem',
                                    color: selected ? opt.color : '#777',
                                    mt: 0.3,
                                    fontWeight: selected ? 700 : 600,
                                }}>
                                    {localize(opt.label)}
                                </SingleLineText>
                            </Box>
                        );
                    })}
                </Box>

                {(medications.length > 0 || selectedMedIds.length > 0) && (
                    <Box sx={{ mb: 1.8, p: 1.25, borderRadius: 3, bgcolor: '#f9fafb', border: '1px solid #f0f0f0' }}>
                        {medications.length > 0 && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, mb: 1 }}>
                                    <SingleLineText sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>{localize("관련 약")}</SingleLineText>
                                    <SingleLineText sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#aaa' }}>{localize("선택")}</SingleLineText>
                                </Box>
                                    <Box
                                        sx={{
                                            display: 'flex', gap: 0.6, overflowX: 'auto',
                                            pb: selectedMedIds.length > 0 ? 1.1 : 0.2,
                                            WebkitOverflowScrolling: 'touch',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}
                                    >
                                        <Box
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                setSelectedMedIds([]);
                                                setEffectDurationPreset(null);
                                                setCustomEffectHours('');
                                                setCustomEffectMinutes('');
                                            }}
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center',
                                                px: 1.4, py: 0.65, borderRadius: '18px',
                                                flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                bgcolor: selectedMedIds.length === 0 ? '#f5f5f5' : '#fff',
                                                color: '#777',
                                                border: selectedMedIds.length === 0 ? '1px solid #e5e7eb' : '1px solid #f0f0f0',
                                                fontWeight: 600,
                                                fontSize: '0.95rem', whiteSpace: 'nowrap',
                                            }}
                                        >{localize("선택 안 함")}</Box>
                                        {medications.map((med) => {
                                            const selected = selectedMedIds.includes(med.id);
                                            return (
                                                <Box
                                                    key={med.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setSelectedMedIds(prev => {
                                                        const next = prev.includes(med.id) ? prev.filter(id => id !== med.id) : [...prev, med.id];
                                                        if (next.length === 0) {
                                                            setEffectDurationPreset(null);
                                                            setCustomEffectHours('');
                                                            setCustomEffectMinutes('');
                                                        }
                                                        return next;
                                                    })}
                                                    sx={{
                                                        display: 'inline-flex', alignItems: 'center',
                                                        px: 1.4, py: 0.65, borderRadius: '18px',
                                                        flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                                        bgcolor: selected ? '#ffe566' : '#fff',
                                                        color: selected ? '#5a4a00' : '#666',
                                                        border: selected ? '1px solid #f4ce26' : '1px solid #f0f0f0',
                                                        fontWeight: selected ? 700 : 600,
                                                        fontSize: '0.95rem', whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {med.name}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                            </>
                        )}

                        {selectedMedIds.length > 0 && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1 }}>
                                    <AccessTimeIcon sx={{ fontSize: 18, color: '#8a7000' }} />
                                    <SingleLineText sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#1a1a1a' }}>{localize("약효가 떨어진 때")}</SingleLineText>
                                    <SingleLineText sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#aaa' }}>{localize("선택")}</SingleLineText>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.6 }}>
                                    {EFFECT_DURATION_OPTIONS.map((option) => {
                                        const selected = effectDurationPreset === option.value;
                                        return (
                                            <Box
                                                key={option.value}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setEffectDurationPreset(selected ? null : option.value)}
                                                sx={{
                                                    minHeight: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    borderRadius: 2, cursor: 'pointer', userSelect: 'none',
                                                    bgcolor: selected ? '#ffe566' : '#fff',
                                                    color: selected ? '#5a4a00' : '#666',
                                                    border: selected ? '1px solid #f4ce26' : '1px solid #ededed',
                                                    fontWeight: selected ? 700 : 600,
                                                    fontSize: '0.92rem',
                                                }}
                                            >
                                                {localize(option.label)}
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {effectDurationPreset === 'CUSTOM' && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            label={localize("시간")}
                                            value={customEffectHours}
                                            onChange={(e) => setCustomEffectHours(e.target.value)}
                                            inputProps={{ min: 0, max: 24, inputMode: 'numeric' }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
                                        />
                                        <TextField
                                            type="number"
                                            size="small"
                                            label={localize("분")}
                                            value={customEffectMinutes}
                                            onChange={(e) => setCustomEffectMinutes(e.target.value)}
                                            inputProps={{ min: 0, max: 59, inputMode: 'numeric' }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                )}

                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', mb: 0.8 }}>{localize("메모")}<Typography component="span" sx={{ ml: 0.7, fontWeight: 600, fontSize: '0.88rem', color: '#aaa' }}>{localize("선택")}</Typography>
                </Typography>
                <TextField
                    fullWidth
                    placeholder={localize("상황이나 느낌을 적어두세요")}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    inputProps={{ maxLength: 1000, 'data-keyboard-lift-action': 'true' }}
                    helperText={`${memo.length}/1000`}
                    sx={{
                        mb: 0,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#f9fafb',
                        },
                    }}
                />

            </BottomSheet>

            {symptoms.length > 0 && (
                <Fab variant="extended" onClick={() => setShowDialog(true)}
                    sx={{
                        position: 'fixed', bottom: 80,
                        right: 'calc(50% - min(240px, 50vw) + 20px)',
                        bgcolor: '#ffe566', color: '#5a4a00',
                        '&:hover': { bgcolor: '#e8d040' },
                        boxShadow: '0 4px 12px rgba(230,168,0,0.4)',
                        fontWeight: 700, fontSize: '0.95rem', gap: 0.5,
                        borderRadius: '28px', px: 2.5,
                    }}>
                    <AddIcon sx={{ fontSize: 20 }} />{localize("증상 기록")}</Fab>
            )}

            {/* 삭제 컨펌 모달 */}
            <Dialog open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', px: 2.5, pt: 2.5, pb: 1 }}>{localize("이 증상 기록을 삭제할까요?")}</DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 0 }}>
                    <DialogContentText sx={{ fontSize: '1rem', color: '#666', lineHeight: 1.55 }}>{localize("삭제하면 되돌릴 수 없어요.")}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pt: 2.5, pb: 2.5, gap: 1 }}>
                    <Button disabled={deleting} onClick={() => setDeleteTarget(null)} variant="text"
                        sx={{
                            color: '#666', fontWeight: 600, fontSize: '1rem', borderRadius: 2, flex: 1, py: 1.1,
                            bgcolor: '#f5f5f5', whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: '#ececec' },
                        }}>{localize("취소")}</Button>
                    <Button disabled={deleting} onClick={() => handleDelete(deleteTarget.id)}
                        variant="contained"
                        sx={{
                            bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '1rem',
                            borderRadius: 2, flex: 1, py: 1.1, whiteSpace: 'nowrap', boxShadow: 'none',
                            '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
                        }}>
                        {deleting ? <LoadingLabel /> : localize("삭제")}
                    </Button>
                </DialogActions>
            </Dialog>

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
            <ScrollDownIndicator />
        </Box>
    );
}
